<template>
    <div class="messagerie">
        <Navigation />
        <v-main>
            <v-container>

                <!-- Modale de consentement RGPD — bloque lecture ET écriture -->
                <v-dialog
                    v-model="showConsentDialog"
                    persistent
                    max-width="600"
                    :retain-focus="false"
                >
                    <v-card>
                        <v-card-title class="primary white--text">
                            <v-icon left color="white">privacy_tip</v-icon>
                            Consentement à l'utilisation de la messagerie
                        </v-card-title>

                        <v-card-text class="pt-5">
                            <p class="body-1">
                                Avant d'accéder à votre messagerie professionnelle onCOGITE, nous vous informons
                                du traitement de vos données personnelles dans ce cadre.
                            </p>

                            <!-- ⚠️ Texte à valider avec le DPO avant mise en production -->
                            <v-alert type="info" outlined class="mb-4">
                                <strong>Traitement de vos données</strong><br><br>
                                Les messages échangés via cette messagerie sont conservés pendant <strong>6 mois</strong>
                                à compter de leur envoi, conformément à notre politique de rétention RGPD.
                                Ils sont accessibles uniquement aux participants de chaque conversation et aux
                                administrateurs de la plateforme en cas de signalement.<br><br>
                                Cette messagerie permet également des <strong>échanges internes entre professionnels</strong>
                                de la plateforme (individuels ou collectifs). Dans ce cadre, votre nom, prénom et adresse
                                de messagerie professionnelle onCOGITE sont visibles des autres professionnels destinataires
                                de vos messages.<br><br>
                                Vous pouvez retirer votre consentement à tout moment en contactant
                                <strong>contact@oncogite.com</strong>. Le retrait du consentement n'affecte pas
                                la licéité du traitement effectué avant celui-ci.
                            </v-alert>

                            <p class="caption grey--text">
                                En cliquant sur "J'accepte", vous consentez à l'utilisation de cette messagerie
                                et au traitement de vos données dans les conditions décrites ci-dessus.
                            </p>
                        </v-card-text>

                        <v-divider></v-divider>

                        <v-card-actions>
                            <v-btn text @click="declineConsent">Refuser</v-btn>
                            <v-spacer></v-spacer>
                            <v-btn
                                color="primary"
                                depressed
                                :loading="consentLoading"
                                @click="acceptConsent"
                            >
                                J'accepte
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

                <!-- Contenu principal (masqué tant que consentement non donné) -->
                <template v-if="hasConsent">
                    <!-- Bouton Nouveau message + layout -->
                    <v-row no-gutters>
                        <v-col class="d-flex justify-end">
                            <v-btn color="primary" depressed @click="openComposer()">
                                <v-icon left>edit</v-icon>
                                Nouveau message
                            </v-btn>
                        </v-col>
                    </v-row>

                    <v-row>
                        <!-- Colonne gauche : liste des conversations -->
                        <v-col cols="12" md="4">
                            <v-card outlined>
                                <v-card-title class="py-2 subtitle-1">
                                    <v-icon left small>forum</v-icon>
                                    Conversations
                                    <v-spacer></v-spacer>
                                    <v-btn icon small :loading="conversationsLoading" @click="loadConversations">
                                        <v-icon small>refresh</v-icon>
                                    </v-btn>
                                </v-card-title>
                                <v-divider></v-divider>

                                <!-- Filtre -->
                                <v-text-field
                                    v-model="searchFilter"
                                    placeholder="Rechercher…"
                                    prepend-inner-icon="search"
                                    dense
                                    hide-details
                                    flat
                                    solo
                                    class="px-2 py-1"
                                ></v-text-field>
                                <v-divider></v-divider>

                                <v-list two-line class="py-0" style="max-height: 520px; overflow-y: auto;">
                                    <template v-if="filteredConversations.length">
                                        <v-list-item
                                            v-for="conv in filteredConversations"
                                            :key="conv.id"
                                            :class="selectedConversationId === conv.id ? ($vuetify.theme.dark ? 'grey darken-3' : 'blue lighten-5') : ''"
                                            @click="selectConversation(conv.id)"
                                            style="cursor:pointer;"
                                        >
                                            <v-list-item-content>
                                                <v-list-item-title class="d-flex align-center">
                                                    <v-badge
                                                        v-if="conv.unread_count > 0"
                                                        :content="conv.unread_count"
                                                        color="primary"
                                                        inline
                                                        class="mr-2"
                                                    ></v-badge>
                                                    <span :class="{ 'font-weight-bold': conv.unread_count > 0 }">
                                                        {{ conv.subject }}
                                                    </span>
                                                </v-list-item-title>
                                                <v-list-item-subtitle>
                                                    {{ participantNames(conv) }}
                                                </v-list-item-subtitle>
                                                <v-list-item-subtitle class="caption grey--text">
                                                    {{ conv.last_message ? formatDate(conv.last_message.sent_at) : '' }}
                                                    <span v-if="conv.last_message"> · {{ conv.last_message.body }}</span>
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </template>
                                    <v-list-item v-else>
                                        <v-list-item-content>
                                            <v-list-item-title class="grey--text text-center py-4">
                                                Aucune conversation
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list>

                                <template v-if="conversationsHasMore">
                                    <v-divider></v-divider>
                                    <div class="text-center py-2">
                                        <v-btn
                                            text
                                            small
                                            color="primary"
                                            :loading="conversationsLoadingMore"
                                            @click="loadMoreConversations"
                                        >
                                            Charger plus
                                        </v-btn>
                                    </div>
                                </template>
                            </v-card>
                        </v-col>

                        <!-- Colonne droite : thread -->
                        <v-col cols="12" md="8">
                            <v-card outlined style="min-height: 400px;">
                                <template v-if="selectedConversation">
                                    <v-card-title class="py-2 subtitle-1">
                                        <v-icon left small>email</v-icon>
                                        {{ selectedConversation.subject }}
                                        <v-spacer></v-spacer>
                                        <span class="caption grey--text">
                                            {{ selectedConversation.participants.map(p => p.firstname + ' ' + p.lastname).join(', ') }}
                                        </span>
                                    </v-card-title>
                                    <v-divider></v-divider>

                                    <!-- Messages du thread -->
                                    <div class="pa-3" style="max-height: 450px; overflow-y: auto;" ref="threadScroll">
                                        <div
                                            v-for="msg in selectedConversation.messages"
                                            :key="msg.id"
                                            class="mb-4 d-flex"
                                            :class="msg.direction === 'out' ? 'justify-end' : 'justify-start'"
                                        >
                                            <div style="max-width: 75%;">
                                                <div class="caption grey--text mb-1"
                                                    :class="msg.direction === 'out' ? 'text-right' : 'text-left'">
                                                    {{ msg.sender ? msg.sender.firstname + ' ' + msg.sender.lastname : 'Expéditeur externe' }}
                                                    · {{ formatDate(msg.sent_at) }}
                                                </div>
                                                <v-card
                                                    :color="msg.direction === 'out' ? 'primary' : ($vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4')"
                                                    :class="msg.direction === 'out' ? 'white--text' : ($vuetify.theme.dark ? 'white--text' : '')"
                                                    flat
                                                    class="pa-3"
                                                    style="border-radius: 12px; white-space: pre-wrap; word-break: break-word;"
                                                >
                                                    {{ msg.body }}
                                                    <!-- Pièces jointes -->
                                                    <div v-if="msg.attachments && msg.attachments.length" class="mt-2 d-flex flex-wrap">
                                                        <v-chip
                                                            v-for="att in msg.attachments"
                                                            :key="att.id"
                                                            small
                                                            outlined
                                                            :color="msg.direction === 'out' ? 'white' : 'grey darken-1'"
                                                            class="mr-1 mb-1"
                                                            style="cursor:pointer;"
                                                            @click="downloadAttachment(att)"
                                                        >
                                                            <v-icon left x-small>attach_file</v-icon>
                                                            {{ att.name }}
                                                            <span class="ml-1 caption">({{ formatSize(att.size) }})</span>
                                                        </v-chip>
                                                    </div>
                                                </v-card>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Zone réponse rapide -->
                                    <v-divider></v-divider>
                                    <div class="pa-3">
                                        <v-file-input
                                            v-model="replyAttachments"
                                            label="Pièces jointes (optionnel)"
                                            prepend-icon="attach_file"
                                            multiple
                                            chips
                                            small-chips
                                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                                            :rules="attachmentRules"
                                            hint="Max 5 fichiers · 10 Mo chacun — PDF, JPG, PNG, Word, Excel"
                                            persistent-hint
                                            truncate-length="25"
                                            dense
                                            class="mb-2"
                                        ></v-file-input>
                                        <div class="d-flex align-end">
                                            <v-textarea
                                                v-model="replyBody"
                                                placeholder="Votre réponse…"
                                                outlined
                                                dense
                                                rows="2"
                                                hide-details
                                                auto-grow
                                                maxlength="10000"
                                                class="mr-2"
                                                @keydown.ctrl.enter="sendReply"
                                            ></v-textarea>
                                            <v-btn
                                                color="primary"
                                                depressed
                                                :loading="replyLoading"
                                                :disabled="!replyBody.trim() || replyAttachmentsInvalid"
                                                @click="sendReply"
                                                style="min-width: 48px; height: 48px;"
                                            >
                                                <v-icon>send</v-icon>
                                            </v-btn>
                                        </div>
                                    </div>
                                </template>

                                <!-- Aucune conversation sélectionnée -->
                                <template v-else>
                                    <div class="d-flex flex-column align-center justify-center" style="height: 400px;">
                                        <v-icon large color="grey lighten-1">email</v-icon>
                                        <p class="grey--text mt-3">Sélectionnez une conversation</p>
                                    </div>
                                </template>
                            </v-card>
                        </v-col>
                    </v-row>

                    <!-- Modale composer — Nouveau message -->
                    <v-dialog v-model="composerDialog" max-width="640" :retain-focus="false">
                        <v-card>
                            <v-card-title class="primary white--text">
                                <v-icon left color="white">edit</v-icon>
                                Nouveau message
                            </v-card-title>
                            <v-card-text class="pt-5">
                                <v-autocomplete
                                    v-model="composerForm.recipients"
                                    :items="recipients"
                                    :loading="recipientsLoading"
                                    item-text="displayName"
                                    item-value="id"
                                    label="Destinataires"
                                    hint="Tapez les premières lettres du nom pour filtrer"
                                    persistent-hint
                                    prepend-inner-icon="person"
                                    outlined
                                    multiple
                                    chips
                                    deletable-chips
                                    small-chips
                                    :menu-props="{ offsetY: true, closeOnContentClick: false }"
                                    no-data-text="Aucun contact disponible"
                                    class="mb-2"
                                ></v-autocomplete>

                                <v-btn
                                    small
                                    text
                                    color="primary"
                                    class="mb-3"
                                    :disabled="proRecipientIds.length === 0"
                                    @click="selectAllPros"
                                >
                                    <v-icon left small>groups</v-icon>
                                    Envoyer à tous les pros
                                </v-btn>


                                <v-text-field
                                    v-model="composerForm.subject"
                                    label="Sujet"
                                    prepend-inner-icon="subject"
                                    outlined
                                    dense
                                    maxlength="255"
                                    counter
                                    class="mb-2"
                                ></v-text-field>

                                <v-textarea
                                    v-model="composerForm.body"
                                    label="Message"
                                    prepend-inner-icon="notes"
                                    outlined
                                    rows="6"
                                    maxlength="10000"
                                    counter
                                    auto-grow
                                ></v-textarea>

                                <!-- Zone pièces jointes -->
                                <v-file-input
                                    v-model="composerForm.attachments"
                                    label="Pièces jointes (optionnel)"
                                    prepend-icon="attach_file"
                                    multiple
                                    chips
                                    small-chips
                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                                    :rules="attachmentRules"
                                    hint="Max 5 fichiers · 10 Mo chacun — PDF, JPG, PNG, Word, Excel"
                                    persistent-hint
                                    truncate-length="25"
                                    class="mt-1"
                                ></v-file-input>
                            </v-card-text>
                            <v-divider></v-divider>
                            <v-card-actions>
                                <v-btn text :disabled="sendLoading" @click="composerDialog = false">Annuler</v-btn>
                                <v-spacer></v-spacer>
                                <v-btn
                                    color="primary"
                                    depressed
                                    :loading="sendLoading"
                                    :disabled="!composerForm.recipients.length || !composerForm.subject || !composerForm.body || attachmentsInvalid"
                                    @click="sendMessage"
                                >
                                    <v-icon left>send</v-icon>
                                    Envoyer
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </template>

            </v-container>
        </v-main>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Navigation from '@/components/Navigation'

export default {
    name: 'Messagerie',
    components: { Navigation },
    data() {
        return {
            consentLoading: false,
            // Conversations
            conversations: [],
            conversationsLoading: false,
            conversationsPage: 1,
            conversationsHasMore: false,
            conversationsLoadingMore: false,
            selectedConversationId: null,
            selectedConversation: null,
            threadLoading: false,
            searchFilter: '',
            searchDebounce: null,
            // Réponse rapide
            replyBody: '',
            replyAttachments: [],
            replyLoading: false,
            // Composer
            composerDialog: false,
            sendLoading: false,
            composerForm: {
                recipients: [],
                subject: '',
                body: '',
                attachments: [],
            },
            recipients: [],
            recipientsLoading: false,
        }
    },
    computed: {
        ...mapGetters(['getProfile']),
        hasConsent() {
            return !!this.getProfile?.mailbox_consent_at
        },
        filteredConversations() {
            // Le filtrage est désormais effectué côté serveur (voir searchFilter watcher)
            return this.conversations
        },
        // La modale s'affiche si et seulement si le consentement n'est pas encore donné.
        // Elle est persistante : impossible de la fermer sans choisir (RGPD).
        showConsentDialog: {
            get() {
                return !this.hasConsent
            },
            set() {
                // Lecture seule — la modale se ferme uniquement via acceptConsent/declineConsent
            }
        },
        attachmentRules() {
            return [
                v => !v || v.length <= 5 || 'Maximum 5 fichiers',
                v => !v || v.every(f => f.size <= 10 * 1024 * 1024) || 'Taille max 10 Mo par fichier',
            ]
        },
        attachmentsInvalid() {
            const files = this.composerForm.attachments
            if (!files || files.length === 0) return false
            return files.length > 5 || files.some(f => f.size > 10 * 1024 * 1024)
        },
        isBulkSend() {
            return this.composerForm.recipients.length > 1
        },
        proRecipientIds() {
            return this.recipients.filter(r => r.type === 'pro').map(r => r.id)
        },
        replyAttachmentsInvalid() {
            const files = this.replyAttachments
            if (!files || files.length === 0) return false
            return files.length > 5 || files.some(f => f.size > 10 * 1024 * 1024)
        },
    },
    watch: {
        hasConsent(val) {
            if (val) this.loadConversations()
        },
        searchFilter() {
            // Debounce : recherche serveur déclenchée 350ms après la dernière frappe
            clearTimeout(this.searchDebounce)
            this.searchDebounce = setTimeout(() => {
                this.loadConversations()
            }, 350)
        },
    },
    mounted() {
        if (this.hasConsent) {
            this.loadConversations()
            // Ouverture automatique du composer si query params (depuis "Envoyer à tous")
            const recipientsParam = this.$route.query.recipients
            const subjectParam = this.$route.query.subject
            if (recipientsParam) {
                const ids = recipientsParam.split(',').map(Number).filter(Boolean)
                if (ids.length) {
                    this.loadRecipients().then(() => {
                        this.composerForm = {
                            recipients: ids,
                            subject: subjectParam || '',
                            body: '',
                            attachments: [],
                        }
                        this.composerDialog = true
                    })
                }
            }
        }
    },
    methods: {
        acceptConsent() {
            this.consentLoading = true
            this.$store.dispatch('mailboxConsentRequest')
                .then(() => {
                    this.$store.commit('setSnack', {
                        type: 'green',
                        msg: 'Consentement enregistré. Bienvenue dans votre messagerie.',
                    })
                })
                .catch(() => {
                    this.$store.commit('setSnack', {
                        type: 'red',
                        msg: 'Une erreur est survenue. Veuillez réessayer.',
                    })
                })
                .finally(() => {
                    this.consentLoading = false
                })
        },
        declineConsent() {
            this.$router.push({ name: 'home' })
        },
        // Conversations
        loadConversations() {
            this.conversationsLoading = true
            this.conversationsPage = 1
            const params = { page: 1, per_page: 10 }
            if (this.searchFilter.trim()) params.search = this.searchFilter.trim()
            this.$axios.get('/api/mailbox/conversations', { params })
                .then(resp => {
                    this.conversations = resp.data.data || []
                    this.conversationsHasMore = !!(resp.data.meta && resp.data.meta.has_more)
                })
                .catch(() => {
                    this.$store.commit('setSnack', { type: 'red', msg: 'Impossible de charger les conversations.' })
                })
                .finally(() => { this.conversationsLoading = false })
        },
        loadMoreConversations() {
            this.conversationsLoadingMore = true
            const nextPage = this.conversationsPage + 1
            const params = { page: nextPage, per_page: 10 }
            if (this.searchFilter.trim()) params.search = this.searchFilter.trim()
            this.$axios.get('/api/mailbox/conversations', { params })
                .then(resp => {
                    this.conversations = this.conversations.concat(resp.data.data || [])
                    this.conversationsPage = nextPage
                    this.conversationsHasMore = !!(resp.data.meta && resp.data.meta.has_more)
                })
                .catch(() => {
                    this.$store.commit('setSnack', { type: 'red', msg: 'Impossible de charger davantage de conversations.' })
                })
                .finally(() => { this.conversationsLoadingMore = false })
        },
        selectConversation(id) {
            if (this.selectedConversationId === id) return
            this.selectedConversationId = id
            this.selectedConversation = null
            this.replyBody = ''
            this.replyAttachments = []
            this.threadLoading = true
            this.$axios.get(`/api/mailbox/conversations/${id}`)
                .then(resp => {
                    this.selectedConversation = resp.data.data
                    // Mettre à jour le compteur non-lus dans la liste locale
                    const conv = this.conversations.find(c => c.id === id)
                    if (conv) conv.unread_count = 0
                    this.$nextTick(() => this.scrollThreadToBottom())
                })
                .catch(() => {
                    this.$store.commit('setSnack', { type: 'red', msg: 'Impossible de charger la conversation.' })
                })
                .finally(() => { this.threadLoading = false })
        },
        scrollThreadToBottom() {
            const el = this.$refs.threadScroll
            if (el) el.scrollTop = el.scrollHeight
        },
        sendReply() {
            if (!this.replyBody.trim() || !this.selectedConversation) return
            this.replyLoading = true
            // Trouver le destinataire (l'autre participant)
            const myId = this.getProfile?.id
            const other = (this.selectedConversation.participants || []).find(p => p.id !== myId)
            const formData = new FormData()
            formData.append('recipient_ids[]', other?.id)
            formData.append('subject', this.selectedConversation.subject)
            formData.append('body', this.replyBody)
            formData.append('conversation_id', this.selectedConversation.id)
            if (this.replyAttachments && this.replyAttachments.length) {
                this.replyAttachments.forEach(file => formData.append('attachments[]', file))
            }
            this.$axios.post('/api/mailbox/messages', formData)
                .then(() => {
                    // Recharger le thread
                    return this.$axios.get(`/api/mailbox/conversations/${this.selectedConversation.id}`)
                })
                .then(resp => {
                    this.selectedConversation = resp.data.data
                    this.replyBody = ''
                    this.replyAttachments = []
                    this.$nextTick(() => this.scrollThreadToBottom())
                })
                .catch(err => {
                    const msg = err.response?.data?.message || 'Une erreur est survenue.'
                    this.$store.commit('setSnack', { type: 'red', msg })
                })
                .finally(() => { this.replyLoading = false })
        },
        participantNames(conv) {
            const myId = this.getProfile?.id
            return (conv.participants || [])
                .filter(p => p.id !== myId)
                .map(p => p.firstname + ' ' + p.lastname)
                .join(', ') || 'Vous'
        },
        formatDate(dateStr) {
            if (!dateStr) return ''
            const d = new Date(dateStr)
            const today = new Date()
            const isToday = d.toDateString() === today.toDateString()
            if (isToday) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
            return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
        },
        // Composer
        openComposer(preselectedIds = []) {
            const ids = Array.isArray(preselectedIds) ? preselectedIds : []
            this.composerForm = { recipients: ids, subject: '', body: '', attachments: [] }
            this.composerDialog = true
            this.loadRecipients()
        },
        loadRecipients() {
            if (this.recipients.length > 0) return Promise.resolve()
            this.recipientsLoading = true
            return this.$axios.get('/api/mailbox/recipients')
                .then(resp => {
                    this.recipients = (resp.data.data || []).map(r => ({
                        ...r,
                        displayName: `${r.lastname} ${r.firstname}${r.type === 'admin' ? ' (Admin)' : r.type === 'pro' ? ' (Pro)' : ''}`,
                    }))
                })
                .catch(() => {
                    this.$store.commit('setSnack', { type: 'red', msg: 'Impossible de charger les contacts.' })
                })
                .finally(() => { this.recipientsLoading = false })
        },
        selectAllPros() {
            const ids = new Set(this.composerForm.recipients)
            this.proRecipientIds.forEach(id => ids.add(id))
            this.composerForm.recipients = Array.from(ids)
        },
        sendMessage() {
            this.sendLoading = true
            const formData = new FormData()
            this.composerForm.recipients.forEach(id => formData.append('recipient_ids[]', id))
            formData.append('subject', this.composerForm.subject)
            formData.append('body', this.composerForm.body)
            if (this.composerForm.attachments && this.composerForm.attachments.length) {
                this.composerForm.attachments.forEach(file => formData.append('attachments[]', file))
            }
            this.$axios.post('/api/mailbox/messages', formData)
                .then(() => {
                    this.composerDialog = false
                    const nb = this.composerForm.recipients.length
                    const msg = nb > 1 ? `Message envoyé à ${nb} destinataires.` : 'Message envoyé.'
                    this.$store.commit('setSnack', { type: 'green', msg })
                    this.loadConversations()
                })
                .catch(err => {
                    const msg = err.response?.data?.message || 'Une erreur est survenue.'
                    this.$store.commit('setSnack', { type: 'red', msg })
                })
                .finally(() => { this.sendLoading = false })
        },
        downloadAttachment(attachment) {
            this.$axios.get(`/api/mailbox/attachments/${attachment.id}/download`, { responseType: 'blob' })
                .then(resp => {
                    const url = window.URL.createObjectURL(new Blob([resp.data]))
                    const link = document.createElement('a')
                    link.href = url
                    link.setAttribute('download', attachment.name)
                    document.body.appendChild(link)
                    link.click()
                    link.remove()
                    window.URL.revokeObjectURL(url)
                })
                .catch(() => {
                    this.$store.commit('setSnack', { type: 'red', msg: 'Impossible de télécharger le fichier.' })
                })
        },
        formatSize(bytes) {
            if (!bytes) return ''
            if (bytes < 1024) return bytes + ' o'
            if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' Ko'
            return (bytes / (1024 * 1024)).toFixed(1) + ' Mo'
        },
    },
}
</script>
